import { gsap } from 'gsap';

export function setCursorBadge(text: string): void {
  const badge = document.getElementById('cursorBadgeText');
  if (!badge) return;
  badge.textContent = text;
}

/**
 * The cursor: the logo's centre triangle, its top-left corner on the pointer (exact, no lag). It keeps a fixed pose.
 * - Expands: links/buttons → 1.6× outline; media → 1.6× with the PLAY / VIEW badge.
 * - Beats: a slow pulse while idle (CSS), one quick beat on click.
 * Shown everywhere incl. the hero (over the fluid ink); hidden on touch and over text fields (the system caret shows).
 */
export function initCursor(): void {
  const el = document.getElementById('triCursor');
  const spin = el?.querySelector<HTMLElement>('.tri-spin');
  const badge = document.getElementById('cursorBadge');
  const badgeText = document.getElementById('cursorBadgeText');
  if (!el || !spin || !window.matchMedia('(hover: hover)').matches) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('tri-cursor');

  const TAIL = 52;                   // px from the tip to just past the grown (1.6×) triangle's tail
  const rot = 0;                      // fixed: the triangle no longer turns with movement
  const setX = gsap.quickSetter(el, 'x', 'px'), setY = gsap.quickSetter(el, 'y', 'px');
  let x = -100, y = -100, lastMove = 0;
  let shownRot = NaN, shownSize = NaN, idle = false, on = false;
  const size = { v: 1 };

  if (badge) gsap.set(badge, { xPercent: 0, yPercent: 0, x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const badgeQ = badge ? {
    x: gsap.quickTo(badge, 'x', { duration: 0.16, ease: 'power2' }),
    y: gsap.quickTo(badge, 'y', { duration: 0.16, ease: 'power2' }),
  } : null;

  // Position is exact (the tip is the hotspot); only class flips that actually change are written
  window.addEventListener('mousemove', (e) => {
    x = e.clientX; y = e.clientY;
    setX(x); setY(y);
    if (!on) { on = true; el.classList.add('on'); }
    if (idle) { idle = false; el.classList.remove('idle'); }
    lastMove = performance.now();
    badgeQ?.x(x + TAIL); badgeQ?.y(y + TAIL); // badge trails at the triangle's tail, not its tip
  }, { passive: true });
  document.addEventListener('mouseleave', () => { on = false; el.classList.remove('on'); });

  gsap.ticker.add(() => {
    const now = performance.now();
    if (!reduce) {
      if (!idle && now - lastMove > 1200) { idle = true; el.classList.add('idle'); }
    }
    // write only when it moved by a visible amount
    if (Math.abs(rot - shownRot) > 0.05 || Math.abs(size.v - shownSize) > 0.001 || Number.isNaN(shownRot)) {
      shownRot = rot; shownSize = size.v;
      gsap.set(spin, { rotation: rot, scale: size.v });
    }
  });

  // Expand over links / media; hide over text fields
  const grow = (s: number) => gsap.to(size, { v: s, duration: 0.45, ease: 'power3.out', overwrite: true }); // ease in place, no bounce
  let state = '';
  document.addEventListener('mouseover', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const field = t.closest('input, textarea, select, [contenteditable]');
    const link = t.closest('a, button, .theme-toggle, label');
    const hit = t.closest<HTMLElement>('.reel-player, .work-card-full, .work-room-hit, .wcf-phone, .work-detail-media, .drum-row, [data-cursor]');
    const media = hit && !(link && hit !== link && hit.contains(link)) ? hit : null;
    const next = field ? 'field' : media ? 'media' : link ? 'link' : '';
    if (next === state) return;
    state = next;
    el.style.visibility = field ? 'hidden' : '';
    el.classList.toggle('link', next === 'link');
    grow(next === 'media' || next === 'link' ? 1.6 : 1);
    if (badge && badgeText) {
      if (media) {
        setCursorBadge(media.dataset.cursor ?? 'VIEW ↗');
        gsap.to(badge, { scale: 1, autoAlpha: 1, duration: 0.25, ease: 'back.out(1.5)', overwrite: 'auto' });
      } else gsap.to(badge, { scale: 0, autoAlpha: 0, duration: 0.2, ease: 'power2.in', overwrite: 'auto' });
    }
  }, { passive: true });

  // One quick beat on click (shrink, overshoot back to the current size)
  window.addEventListener('mousedown', () => {
    if (reduce) return;
    const base = state === 'media' || state === 'link' ? 1.6 : 1;
    gsap.timeline({ overwrite: true })
      .to(size, { v: base * 0.86, duration: 0.1, ease: 'power2.out' })
      .to(size, { v: base, duration: 0.45, ease: 'power3.out' });
  });
}
